import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RoleModel, RoleResponse } from '../models/Role';
import { getApiBaseUrl } from '@/setup/apiBaseUrl';

export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, { getState }) => {
      // Get token from Redux state if needed
      const state = getState() as any;
      const accessToken = state.auth?.accessToken;
      
      // Always add Authorization header if we have a token
      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`);
      }
      
      headers.set('content-type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Role'],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: (builder) => ({
    // Get roles list
    getRoles: builder.query<RoleResponse, { search?: string; is_active?: boolean; level?: string; company?: string; ordering?: string; page?: number; page_size?: number } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          if (params.search) {
            searchParams.append('search', params.search);
          }
          if (params.is_active !== undefined) {
            searchParams.append('is_active', params.is_active.toString());
          }
          if (params.level && params.level !== 'all') {
            searchParams.append('level', params.level);
          }
          if (params.company && params.company !== 'all') {
            searchParams.append('company', params.company);
          }
          if (params.ordering) {
            searchParams.append('ordering', params.ordering);
          }
          if (params.page) {
            searchParams.append('page', String(params.page));
          }
          if (params.page_size) {
            searchParams.append('page_size', String(params.page_size));
          }
        }
        // Exclude users list from roles response to optimize payload
        searchParams.append('exclude_users', 'true');
        
        const queryString = searchParams.toString();
        return `accounts/roles/${queryString ? '?' + queryString : ''}`;
      },
      providesTags: ['Role'],
    }),
    
    // Get role by ID
    getRoleById: builder.query<RoleModel, string>({
      query: (id) => `accounts/roles/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),
    
    // Create role
    createRole: builder.mutation<RoleModel, Partial<RoleModel>>({
      query: (roleData) => ({
        url: 'accounts/roles/',
        method: 'POST',
        body: roleData,
      }),
      invalidatesTags: ['Role'],
    }),
    
    // Update role
    updateRole: builder.mutation<RoleModel, { id: string; data: Partial<RoleModel> }>({
      query: ({ id, data }) => ({
        url: `accounts/roles/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Role'],
    }),
    
    // Delete role
    deleteRole: builder.mutation<void, string>({
      query: (id) => ({
        url: `accounts/roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Role', id }],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = rolesApi;
